import clsx from 'clsx';
import { format } from 'date-fns';
import { FullEduHubArticleResult } from 'types/studio/eduHub';
import { toPlainText } from '@portabletext/react';
import { Typography, LinkToBack } from '@ag1/ui';
import { CustomPortableText } from 'shared/studio/PortableText';
import { StudioHotspotImageV2 } from 'shared/studio/StudioHotspotImage/StudioHotspotImage';
import SocialShare from 'shared/components/SocialShare/SocialShare';
import { getSitemapHref } from 'appLayer/sitemap/utils/getSitemapHref';
import { routes } from '@portal/constants/routes';
import { useLocale } from 'shared/hooks/useLocale';
import { EduArticleHeroYoutubeVideo } from './components/EduArticleHeroYoutubeVideo';
import styles from './EduArticleHero.module.scss';

interface Props {
  article: FullEduHubArticleResult;
}

export const EduArticleHero = ({ article }: Props) => {
  const locale = useLocale();
  const { eduHub } = routes;
  const { headline, subheading, coverImage, heroVideo, content } = article;

  const { slug } = article.category;

  const getArticleWordsCount = () => {
    const textArray: string[] = [];

    content?.forEach((item) => {
      if (item._type === 'eduPortableText' && item.portableText) {
        textArray.push(toPlainText(item.portableText));
      }
    });

    return textArray.join(' ').split(/\s+/).length;
  };

  const readingTime = Math.ceil(getArticleWordsCount() / 225);

  const dateObject = new Date(article?.date as string | number | Date);
  const formattedDate = format(dateObject, 'MM/dd/yyyy');

  return (
    <section className={styles.root}>
      <div className={styles.topRow}>
        {slug?.current && (
          <LinkToBack
            href={eduHub}
            title="Back"
            className={styles.backButton}
            variantColor={article?.backLinkColor}
          />
        )}
        <SocialShare
          url={getSitemapHref({ slug: article.slug.current, type: 'eduArticles', locale })}
          title={headline || article.seo?.title || ''}
          theme="new"
        />
      </div>
      {heroVideo?.url && <EduArticleHeroYoutubeVideo heroVideo={heroVideo} mobile />}
      {!heroVideo?.url && coverImage && (
        <StudioHotspotImageV2
          image={coverImage}
          className={clsx(styles.imageMobile, styles.image)}
          eagerLoading
        />
      )}

      <div className={styles.headingBlock}>
        <Typography
          variant="headlineSans"
          size="2"
          component="h1"
          noPadding
          color="black-new"
          className={styles.headline}>
          {headline}
        </Typography>
        {subheading && (
          <Typography
            variant="portalSubtitle"
            size="2"
            component="div"
            noPadding
            color="black-new"
            className={styles.subtitle}>
            <CustomPortableText value={subheading} />
          </Typography>
        )}
      </div>
      <div className={styles.information}>
        <div className={styles.authorBlock}>
          {article.reviewedBy?.name && (
            <Typography variant="portalText" size="1" component="span" noPadding color="black-new">
              {`${article.reviewedByLabel} ${article.reviewedBy.name}`}
            </Typography>
          )}
          {article.writtenBy?.name && (
            <Typography variant="portalText" size="1" component="span" noPadding color="black-new">
              {`${article.writtenByLabel} ${article.writtenBy?.name}`}
            </Typography>
          )}
        </div>

        <div className={styles.dateBlock}>
          <Typography
            variant="body"
            size="3"
            component="span"
            noPadding
            className={styles.dateText}>
            {`${readingTime} MIN READ — `}
            {article.dateText ? `${article.dateText} — ${formattedDate}` : formattedDate}
          </Typography>
        </div>
      </div>
      {heroVideo?.url && <EduArticleHeroYoutubeVideo heroVideo={heroVideo} />}
      {!heroVideo?.url && coverImage && (
        <StudioHotspotImageV2
          image={coverImage}
          className={clsx(styles.image, styles.imageDesktop)}
        />
      )}
    </section>
  );
};
